import * as React from "react";
import { Timer } from "lucide-react";
import { Button } from "../../ui/button.js";

// ---------------------------------------------------------------------------
// formatMs helper — exported so tests and TimerTray can share it
// ---------------------------------------------------------------------------

/**
 * Formats a millisecond duration as "m:ss" (minutes : zero-padded seconds).
 * Negative values are clamped to 0.
 *
 * Examples:
 *   formatMs(65000)  → "1:05"
 *   formatMs(5000)   → "0:05"
 *   formatMs(-100)   → "0:00"
 */
export function formatMs(ms: number): string {
  const totalSec = Math.max(0, Math.floor(ms / 1_000));
  const minutes = Math.floor(totalSec / 60);
  const seconds = totalSec % 60;
  return `${minutes}:${String(seconds).padStart(2, "0")}`;
}

// ---------------------------------------------------------------------------
// TimerPill
// ---------------------------------------------------------------------------

export interface TimerPillProps {
  /** Number of active timers; pill is hidden when 0. */
  count: number;
  /** Remaining ms for the soonest timer; null if none running. */
  shortestRemainingMs: number | null;
  /** Called when the pill is tapped to open the timer tray. */
  onOpen: () => void;
}

/**
 * Floating pill (top-right) that summarises running timers.
 * Hidden when count === 0.
 * Shows: timer icon | count | m:ss countdown of the shortest timer.
 * Tap target ≥ 48 px.
 */
export function TimerPill({ count, shortestRemainingMs, onOpen }: TimerPillProps) {
  if (count === 0) return null;

  const countdownText =
    shortestRemainingMs !== null ? formatMs(shortestRemainingMs) : "—";

  return (
    <Button
      onClick={onOpen}
      aria-label={`${count} timer${count !== 1 ? "s" : ""} running, ${countdownText} remaining — tap to manage`}
      size="cook"
      variant="secondary"
      className="fixed top-4 right-4 z-50 flex items-center gap-2 rounded-full px-4 shadow-lg"
    >
      <Timer className="size-5 shrink-0" aria-hidden />
      <span className="text-base font-semibold">{count}</span>
      <span className="text-base tabular-nums">{countdownText}</span>
    </Button>
  );
}
